import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const steps = [
  { num: '01', title: 'Agendamento', desc: 'Escolha o melhor horário para você através do nosso WhatsApp ou telefone. Resposta rápida e sem burocracia.' },
  { num: '02', title: 'Consulta Humanizada', desc: 'No dia da sua consulta, você será recebida em um ambiente acolhedor. A Dra. Nathália dedica tempo para ouvir sua história.' },
  { num: '03', title: 'Diagnóstico', desc: 'Com tecnologia moderna e olhar atento, realizamos os exames necessários com máximo conforto e precisão.' },
  { num: '04', title: 'Tratamento Personalizado', desc: 'Cada plano de cuidado é único, construído junto com você, respeitando suas necessidades e seu estilo de vida.' },
  { num: '05', title: 'Acompanhamento', desc: 'Sua jornada de saúde continua. Mantemos contato próximo para garantir que você se sinta cuidada em cada etapa.' },
];

export default function JourneySection() {
  const sectionRef = useRef<HTMLElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const mobilePathRef = useRef<SVGPathElement>(null);
  const dotRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const paths = [pathRef.current, mobilePathRef.current].filter(Boolean) as SVGPathElement[];
    const section = sectionRef.current;
    if (!paths.length || !section) return;

    const ctx = gsap.context(() => {
      paths.forEach((path) => {
        const pathLength = path.getTotalLength();

        // Set up the dash for drawing
        path.style.strokeDasharray = `${pathLength}`;
        path.style.strokeDashoffset = `${pathLength}`;
        path.style.transition = 'none';

        // Draw the SVG line as user scrolls
        gsap.to(path, {
          strokeDashoffset: 0,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'top 70%',
            end: 'bottom 30%',
            scrub: 0.8,
          },
        });
      });

      // Each dot and card appears in sequence
      dotRefs.current.forEach((dot, i) => {
        if (!dot || !cardRefs.current[i]) return;

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: cardRefs.current[i],
            start: 'top 85%',
            end: 'top 35%',
            toggleActions: 'play none none reverse',
          },
        });

        tl.fromTo(dot, { scale: 0, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(2)' })
          .fromTo(cardRefs.current[i]!, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }, '-=0.3');
      });
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-cascade
      className="relative w-full py-28 md:py-44 bg-nude paper-texture overflow-hidden"
    >
      {/* Organic background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[10%] right-[-3%] w-[30%] h-[40%] rounded-full bg-sage-light/6 blur-[120px]" />
        <div className="absolute bottom-[5%] left-[-5%] w-[25%] h-[35%] rounded-full bg-rose-light/8 blur-[100px]" />
      </div>

      <div className="relative max-w-[1100px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16 md:mb-24">
          <p data-cascade-label className="font-body text-[10px] md:text-[11px] text-secondary-500 tracking-[0.4em] uppercase mb-3">
            Jornada da Paciente
          </p>
          <div data-cascade-line className="w-12 h-[1px] bg-gold-matte/40 mx-auto mb-4" />
          <h2 data-cascade-title className="font-heading text-2xl md:text-3xl lg:text-4xl text-graphite font-light">
            Do primeiro contato ao{' '}
            <span className="italic">cuidado contínuo</span>.
          </h2>
        </div>

        {/* SVG Line + Steps */}
        <div className="relative">
          {/* Desktop SVG path */}
          <svg
            className="hidden md:block absolute left-1/2 -translate-x-1/2 top-0 w-[2px]"
            style={{ height: 'calc(100% - 80px)' }}
            viewBox="0 0 2 600"
            preserveAspectRatio="none"
          >
            <path
              ref={pathRef}
              d="M1 0 C1 60, 1 120, 1 120 C1 180, 1 240, 1 240 C1 300, 1 360, 1 360 C1 420, 1 480, 1 480 C1 540, 1 600, 1 600"
              fill="none"
              stroke="url(#journeyGradient)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="journeyGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#C4A675" stopOpacity="0.2" />
                <stop offset="30%" stopColor="#C4A675" stopOpacity="0.6" />
                <stop offset="60%" stopColor="#E8D9D8" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#B7C4B3" stopOpacity="0.3" />
              </linearGradient>
            </defs>
          </svg>

          {/* Mobile SVG path — draws as you scroll */}
          <svg
            className="md:hidden absolute left-[19px] top-0 w-[2px]"
            style={{ height: 'calc(100% - 80px)' }}
            viewBox="0 0 2 600"
            preserveAspectRatio="none"
          >
            <path
              ref={mobilePathRef}
              d="M1 0 L1 600"
              fill="none"
              stroke="url(#journeyGradientMobile)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <defs>
              <linearGradient id="journeyGradientMobile" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#C4A675" stopOpacity="0.2" />
                <stop offset="30%" stopColor="#C4A675" stopOpacity="0.6" />
                <stop offset="60%" stopColor="#E8D9D8" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#B7C4B3" stopOpacity="0.3" />
              </linearGradient>
            </defs>
          </svg>

          {/* Steps */}
          <div className="flex flex-col gap-20 md:gap-28">
            {steps.map((step, index) => {
              const isLeft = index % 2 === 0;
              return (
                <div
                  key={step.num}
                  className={`relative flex items-start gap-8 md:gap-0 ${
                    isLeft ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Dot on the line */}
                  <div
                    ref={(el) => { dotRefs.current[index] = el; }}
                    className="absolute left-[12px] md:left-1/2 md:-translate-x-1/2 w-[10px] h-[10px] rounded-full bg-gold-matte/50 border-2 border-background-50 z-10"
                    style={{ boxShadow: '0 0 12px rgba(196, 166, 117, 0.2)' }}
                  />

                  {/* Content card */}
                  <div
                    ref={(el) => { cardRefs.current[index] = el; }}
                    className={`pl-10 md:pl-0 md:w-[42%] ${
                      isLeft ? 'md:pr-12 md:text-right' : 'md:pl-12'
                    }`}
                  >
                    <span className="font-heading text-3xl md:text-4xl text-gold-matte/25 font-light block mb-2">
                      {step.num}
                    </span>
                    <h3 className="font-heading text-xl md:text-2xl text-graphite font-light mt-1 mb-4">
                      {step.title}
                    </h3>
                    <p className="font-body text-sm text-graphite/50 leading-[1.8] font-light max-w-sm mx-auto md:mx-0"
                       style={isLeft ? { marginRight: 0, marginLeft: 'auto' } : {}}>
                      {step.desc}
                    </p>
                  </div>

                  {/* Spacer for the other side */}
                  <div className="hidden md:block md:w-[42%]" />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}